/**
 * The Action Centre's card ordering (EO-1287), as agreed with the business team:
 *
 *   Campaign → Promo Expiry → Corridor Request → Alignment → Corridor → LOS → DOW → LT → Pace → ARI
 *   → Cancellation → Config, then any theme with no slot of its own
 *
 * The v2 half of that is Alex's ranking (Alignment, LOS, DOW, LT, ARI, Cancellation, Config)
 * with Pace slotted in after LT; the corridor and campaign cards keep their original v1
 * positions.
 *
 * Every card, v1 or v2, is filed into one of the groups in {@link CARD_SEQUENCE}, and the feed is
 * emitted by walking that array. A card's GROUP decides where it renders, not its card_rank —
 * rank only orders the v2 cards inside one group.
 */

// THIS ARRAY IS THE ORDERING SPEC: to move a category, move its entry here.
export const CARD_SEQUENCE = [
  // EO-1891: the Black Friday offer leads the feed for the campaign's duration.
  "BLACK_FRIDAY",
  "CAMPAIGN",
  // v1-only: the expiring-promo card sits between campaign and corridor requests.
  "PROMO_EXPIRY",
  "CORRIDOR_REQUEST",
  "ALIGNMENT",
  "CORRIDOR",
  "LOS",
  "DOW",
  "LEAD_TIME",
  "PACE",
  "ARI",
  // Both are Review Rate Plans cards, and the ranking puts them last in this order.
  "CANCELLATION",
  "CONFIG",
  // Themes with no slot of their own (promo, anything new) sit below all of it.
  "OTHER",
];

/**
 * Which group a v2 card belongs to, from its `theme` column. availability joins ARI (same
 * availability/inventory problem); Campaign, CorridorRequest and Corridor have no v2 counterpart
 * and stay pure v1.
 */
const V2_GROUP_BY_THEME = {
  black_friday: "BLACK_FRIDAY",
  alignment: "ALIGNMENT",
  alignment_intelligence: "ALIGNMENT",
  los: "LOS",
  dow: "DOW",
  lead_time: "LEAD_TIME",
  pace: "PACE",
  availability: "ARI",
  cancellation: "CANCELLATION",
  config: "CONFIG",
};

/**
 * Themes with no slot of their own: `promo`, and anything the pipeline adds later. They collect
 * here, at the foot of the feed, rather than under a heading that does not describe them.
 */
export const V2_UNGROUPED = "OTHER";

const normalizeTheme = (theme) =>
  String(theme ?? "")
    .trim()
    .toLowerCase();

/** A v2 card's group. Unknown and missing themes fall to {@link V2_UNGROUPED}, never to a real one. */
export const v2GroupOf = (card) =>
  V2_GROUP_BY_THEME[normalizeTheme(card?.theme)] ?? V2_UNGROUPED;

/**
 * V2 supersedes V1: a v2 card on one of these themes carries the same
 * recommendation as the v1 card of the mapped group, so rendering both would
 * duplicate it — the v1 card is suppressed instead. Deliberately narrower than
 * V2_GROUP_BY_THEME: Alignment keeps both cards (not in the agreed match
 * rules), and Campaign / Corridor / Corridor Request have no v2 counterpart.
 */
const V1_GROUP_SUPERSEDED_BY_THEME = {
  availability: "ARI",
  los: "LOS",
  dow: "DOW",
  lead_time: "LEAD_TIME",
};

/**
 * The v1 groups whose cards duplicate a present v2 card and must not render.
 * Presence-based: a dismissed v2 card still suppresses its v1 counterpart —
 * dismissing "the recommendation" should not resurface an older copy of it.
 */
export const supersededV1Groups = (v2Cards) => {
  const groups = new Set();
  (v2Cards || []).forEach((card) => {
    const group = V1_GROUP_SUPERSEDED_BY_THEME[normalizeTheme(card?.theme)];
    if (group) groups.add(group);
  });
  return groups;
};

/**
 * The v1 promo `type` (API group name) that carries the Alignment recommendation,
 * filed under the ALIGNMENT slot of CARD_SEQUENCE by ActionCenter.
 */
const V1_ALIGNMENT_TYPE = "ALIGNMENT_INTELLIGENCE";

/**
 * Whether the v1 feed contains an Alignment card. Reads the same
 * PROMO / ALIGNMENT_INTELLIGENCE groups ActionCenter files under the ALIGNMENT
 * group, so presence here matches "a v1 alignment card would render". Like
 * {@link supersededV1Groups} it is presence-based: a dismissed v1 card still
 * counts, so dismissing the recommendation never resurfaces the v2 copy of it.
 */
export const hasV1AlignmentCard = (v1Data) =>
  (v1Data?.results?.PROMO || []).some((g) => g?.type === V1_ALIGNMENT_TYPE);

/**
 * V1 supersedes V2 for Alignment (EO-1679): the v1 Alignment Intelligence card is
 * the authoritative one, so a v2 card on the alignment theme for the SAME property
 * duplicates it and must be suppressed — the inverse of {@link supersededV1Groups},
 * applied to the one theme the business wants v1 to win.
 *
 * The property guard keeps a v1 alignment card for one hotel from ever hiding a v2
 * card belonging to another: each feed carries its own propertyId, and only a
 * match counts as the same recommendation. Both ids are absent → nothing is
 * suppressed (we cannot prove they are the same property).
 */
export const isV2AlignmentSupersededByV1 = (v1Data, v2Data) => {
  if (!hasV1AlignmentCard(v1Data)) return false;
  const v1PropertyId = v1Data?.propertyId;
  const v2PropertyId = v2Data?.propertyId;
  return (
    v1PropertyId != null &&
    v2PropertyId != null &&
    v1PropertyId === v2PropertyId
  );
};

/**
 * Flattens `{ GROUP: [card, ...] }` into the rendered order. Groups with no cards are skipped, so
 * a hotel with one category renders one card.
 *
 * A group key that is NOT in CARD_SEQUENCE is dropped silently — which is why
 * everyV2GroupIsInTheSequence is tested: a typo in V2_GROUP_BY_THEME would lose cards with no
 * error anywhere.
 */
export const orderBySequence = (byGroup) =>
  CARD_SEQUENCE.flatMap((group) => byGroup?.[group] || []);

/**
 * Moves the cards whose key matches `pinnedKey` to the front (EO-1893: the card an
 * email deep link names renders first). No match leaves the order untouched.
 */
export const pinCardFirst = (cards, pinnedKey) => {
  if (!pinnedKey) return cards;
  const pinned = cards.filter((card) => card?.key === pinnedKey);
  if (pinned.length === 0) return cards;
  return [...pinned, ...cards.filter((card) => card?.key !== pinnedKey)];
};

/** The ActionCenter key of a v2 card, for pinning it; null without an id. */
export const llmCardKey = (recommendationId) =>
  recommendationId ? `llm-${recommendationId}` : null;

/** Whether `card` is the v2 card a CTA request (strip / pop-up) names. */
export const isRequestedCard = (card, recommendationId) =>
  recommendationId != null && card?.recommendationId === recommendationId;

/** Every group a v2 card can be filed into — used to prove they all appear in CARD_SEQUENCE. */
export const V2_GROUPS = [
  ...new Set([...Object.values(V2_GROUP_BY_THEME), V2_UNGROUPED]),
];
